import { ProductAnalyticsPeriod, ProductAnalyticsPlatform } from './types'

export const PERIODS: ProductAnalyticsPeriod[] = ['TODAY', 'LAST_7_DAYS', 'LAST_30_DAYS', 'LAST_90_DAYS']

export const DEFAULT_PERIOD: ProductAnalyticsPeriod = 'LAST_30_DAYS'

export const PLATFORMS: ProductAnalyticsPlatform[] = ['WEB', 'MOBILE']

export const DEFAULT_PLATFORM: ProductAnalyticsPlatform = 'WEB'

// Mobile sections are the first segment of the app's screen names; unknown ones are displayed as is.
export const MOBILE_SCREEN_LABEL_KEYS: Record<string, string> = {
  '/home': 'home',
  '/code': 'code',
  '/code_create': 'code_create',
  '/code_details': 'code_details',
  '/code_filter': 'code_filter',
  '/map': 'map',
  '/ran': 'private_wifi',
  '/tvAndChromecast': 'tv_and_chromecast',
  '/user': 'user',
  '/login': 'login',
  '/two_factor_auth': 'two_factor_auth',
  '/onboarding': 'onboarding',
}

export const CHART_COLORS = {
  primary: '#4F8AA4',
  secondary: '#A5D9CB',
  tertiary: '#EC7A74',
  quaternary: '#59B5DA',
  quinary: '#F2B263',
  senary: '#8E7CC3',
  septenary: '#7FB069',
  octonary: '#D97AB0',
}

export const MODULE_SERIES = [
  { dataKey: 'codes', color: CHART_COLORS.primary },
  { dataKey: 'users', color: CHART_COLORS.secondary },
  { dataKey: 'privateWifi', color: CHART_COLORS.tertiary },
  { dataKey: 'eventWifi', color: CHART_COLORS.quaternary },
  { dataKey: 'accessPointZones', color: CHART_COLORS.septenary },
  { dataKey: 'wan', color: CHART_COLORS.quinary },
  { dataKey: 'bypass', color: CHART_COLORS.senary },
  { dataKey: 'streamCast', color: CHART_COLORS.octonary },
] as const

export const AXIS_TICK = { fontSize: 10, fill: '#6B7280' }

export const TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid #E5E7EB',
  fontSize: 11,
}

const MONDAY_REFERENCE = new Date(2024, 0, 1)

export const formatWeekday = (weekday: string, language: string): string => {
  const date = new Date(MONDAY_REFERENCE)
  date.setDate(MONDAY_REFERENCE.getDate() + Number(weekday) - 1)
  return date.toLocaleDateString(language, { weekday: 'short' })
}

// Hourly buckets (today) are formatted "YYYY-MM-DD HH:00:00", daily and weekly ones "YYYY-MM-DD".
export const formatPeriodStart = (periodStart: string, language: string): string => {
  const [date, time] = periodStart.split(' ')
  if (time) return `${Number(time.slice(0, 2))}h`

  return new Date(`${date}T00:00:00`).toLocaleDateString(language, { day: '2-digit', month: '2-digit' })
}

export const formatNumber = (value: number, language: string, maximumFractionDigits = 1): string => (
  new Intl.NumberFormat(language, { maximumFractionDigits }).format(value)
)

export const formatStatValue = (value: number | null | undefined, language: string, suffix = ''): string => {
  if (value === null || value === undefined) return '—'

  return `${formatNumber(value, language)}${suffix}`
}

export const formatDuration = (totalSeconds: number): string => {
  const roundedSeconds = Math.round(totalSeconds)
  const hours = Math.floor(roundedSeconds / 3600)
  const minutes = Math.floor((roundedSeconds % 3600) / 60)
  const seconds = roundedSeconds % 60

  if (hours > 0) return `${hours} h ${String(minutes).padStart(2, '0')} min`
  if (minutes > 0) return `${minutes} min ${String(seconds).padStart(2, '0')} s`

  return `${seconds} s`
}

export const hasNonZeroValue = <T extends object>(rows: T[] | null | undefined, keys: (keyof T)[]): boolean => (
  Boolean(rows?.some(row => keys.some(key => Number(row[key]) > 0)))
)
